import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getFirestore, doc, getDoc } from 'firebase/firestore';
import { useTranslation } from 'react-i18next';
import { 
  FaArrowLeft,
  FaUser,
  FaEnvelope,
  FaPhone,
  FaMapMarkerAlt,
  FaStar,
  FaCalendarAlt,
  FaBriefcase,
  FaGraduationCap,
  FaTools,
  FaCheck,
  FaHardHat,
  FaRegHospital,
  FaEye,
  FaTrophy,
  FaIdCard,

} from 'react-icons/fa';
import { QRCodeSVG } from 'qrcode.react';
import ExportPDFButton from '../components/ExportPDFButton';

interface Skill {
  id: string;
  name: string;
  description: string;
  level: 'beginner' | 'intermediate' | 'advanced' | 'expert';
  category: 'technical' | 'soft' | 'safety' | 'certification' | 'other';
  yearsOfExperience: number;
  isCertified: boolean;
  certificationDetails?: string;
  createdAt: string;
}

interface UserProfile {
  // Información básica
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  sector: 'health' | 'construction';
  role: 'worker' | 'premiumWorker' | 'specialWorker';
  status: 'active' | 'pending';
  language: string;
  
  // Foto de perfil
  photoURL: string;
  
  // QR Code del perfil público
  qrCodeUrl?: string;
  
  // Dirección
  address: {
    street: string;
    city: string;
    state: string;
    zipCode: string;
    country: string;
    apartment?: string;
    number?: string;
    verificationStatus?: 'pending' | 'approved' | 'rejected';
  };
  
  // Códigos de ubicación
  cityCode?: string;
  stateCode?: string;
  countryCode?: string;
  
  // Información adicional
  additionalInfo?: string;
  personalDescription?: string;
  
  // Experiencia
  experience: {
    years: number;
    skills: Array<{
      id: string;
      categoryId: string;
      name_en: string;
      name_es: string;
    }>;
    description: string;
  };
  
  // Certificaciones
  certifications: Array<{
    id?: string;
    name: string;
    name_en?: string;
    name_es?: string;
    issuer: string;
    issuer_en?: string;
    issuer_es?: string;
    date: string;
    expiryDate?: string;
    status: 'approved' | 'rejected' | 'pending';
    fileUrl?: string;
    frontImageUrl?: string;
    backImageUrl?: string;
    description_en?: string;
    description_es?: string;
    duration_en?: string;
    duration_es?: string;
    certificationId?: string;
    uploadedAt?: string;
  }>;
  
  // Identificación
  identification?: {
    type: string;
    number: string;
    frontImageUrl: string;
    backImageUrl: string;
    verificationStatus: 'pending' | 'approved' | 'rejected';
    updatedAt: string;
  };
  
  // Ubicación
  lastKnownLocation?: {
    lat: number;
    lon: number;
    timestamp: string;
  };
  
  // Estados de verificación
  verificationRead?: boolean;
  verificationReadAt?: string;
  
  // Estados de rechazo
  addressRejected?: boolean;
  addressRejectedAt?: string;
  addressRejectedBy?: string;
  addressRejectionNotes?: string;
  
  identificationRejected?: boolean;
  identificationRejectedAt?: string;
  identificationRejectedBy?: string;
  identificationRejectionNotes?: string;
  
  // Estados de bloqueo
  blockedAt?: string;
  blockedBy?: string;
  blockReason?: string | null;
  blockReasonDetails?: string | null;
  unblockedAt?: string;
  unblockedBy?: string;
  
  // Onboarding
  onBoarding: boolean;
  completedSteps: string[];
  
  // Timestamps
  createdAt: string;
  updatedAt: string;
  
  // Campos legacy (mantener por compatibilidad)
  skills?: Skill[];
  completedJobs?: number;
  rating?: number;
  reviews?: number;
  lastActive?: string;
  availableFunds?: number;
  totalEarnings?: number;
}

const WorkerPublicProfile: React.FC = () => {
  const { workerId } = useParams<{ workerId: string }>();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const db = getFirestore();
  
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [languageChanged, setLanguageChanged] = useState(false);

  useEffect(() => {
    // Verificar si hay un parámetro de idioma en la URL
    const urlParams = new URLSearchParams(window.location.search);
    const langParam = urlParams.get('lang');
    
    // Si hay un parámetro de idioma y es diferente al actual, cambiar el idioma
    if (langParam && (langParam === 'es' || langParam === 'en') && langParam !== i18n.language) {
      i18n.changeLanguage(langParam);
      setLanguageChanged(true);
    }
    
    if (workerId) {
      fetchPublicProfile();
    }
  }, [workerId, i18n]);

  // Efecto para manejar el cambio de idioma
  useEffect(() => {
    if (languageChanged) {
      // Pequeño delay para asegurar que el idioma se haya cambiado completamente
      const timer = setTimeout(() => {
        setLanguageChanged(false);
      }, 100);
      
      return () => clearTimeout(timer);
    }
  }, [languageChanged]);

  const fetchPublicProfile = async () => {
    if (!workerId) return;
    
    try {
      setLoading(true);
      const userRef = doc(db, 'users', workerId);
      const userSnap = await getDoc(userRef);
      
      if (userSnap.exists()) {
        const userData = userSnap.data() as UserProfile;
        setProfile(userData);
      } else {
        setError(t('publicProfile.error.notFound'));
      }
    } catch (error) {
      console.error('Error fetching public profile:', error);
      setError(t('publicProfile.error.loading'));
    } finally {
      setLoading(false);
    }
  };



  const approvedCertifications = profile?.certifications?.filter(cert => cert.status === 'approved') || [];

  // Función para obtener el nombre de la categoría
  const getCategoryName = (categoryId: string) => {
    const categories: Record<string, Record<string, { en: string; es: string }>> = {
      construction: {
        cat001: { en: "General Construction", es: "Construcción General" },
        cert001: { en: "General Safety Certifications", es: "Certificaciones Generales de Seguridad" },
        cert002: { en: "Environmental Hazard Certifications", es: "Certificaciones de Riesgos Ambientales" },
        cert003: { en: "Mold Certifications", es: "Certificaciones de Moho" },
        cert004: { en: "Worksite Safety Certifications", es: "Certificaciones de Seguridad en el Sitio" }
      },
      health: {
        cat001: { en: "General Health", es: "Salud General" }
      }
    };

    const sectorCategories = categories[profile?.sector || 'construction'];
    const category = sectorCategories?.[categoryId];

    if (category) {
      return i18n.language === 'es' ? category.es : category.en;
    }

    return categoryId; // Fallback al ID si no se encuentra la categoría
  };

  const getSectorIcon = (sector: string) => {
    return sector === 'construction' ? <FaHardHat className="text-orange-500" /> : <FaRegHospital className="text-blue-500" />;
  };

  const getRoleBadge = (role: string) => {
    const roleConfig = {
      worker: { color: 'bg-blue-100 text-blue-800', text: t('publicProfile.roles.worker') },
      premiumWorker: { color: 'bg-purple-100 text-purple-800', text: t('publicProfile.roles.premiumWorker') },
      specialWorker: { color: 'bg-red-100 text-red-800', text: t('publicProfile.roles.specialWorker') }
    };
    
    const config = roleConfig[role as keyof typeof roleConfig] || roleConfig.worker;
    return (
      <span className={`px-3 py-1 rounded-full text-sm font-medium ${config.color}`}>
        {config.text}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-red-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto mb-4"></div>
          <p className="text-gray-600">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-red-100 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600">{error || t('publicProfile.error.notFound')}</p>
          <button
            onClick={() => navigate(-1)}
            className="mt-4 flex items-center gap-2 text-red-600 hover:text-red-700 transition-colors"
          >
            <FaArrowLeft />
            {t('common.goBack')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-red-100 p-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-red-600 hover:text-red-700 transition-colors"
          >
            <FaArrowLeft />
            <span>{t('common.goBack')}</span>
          </button>
          
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <FaEye className="text-gray-400" />
              <span className="text-sm text-gray-500">{t('publicProfile.publicView')}</span>
            </div>
            
            {/* Botón de exportación a PDF */}
            <ExportPDFButton 
              profile={profile}
              userId={workerId}
              variant="outline"
              size="sm"
            />
          </div>
        </div>

        {/* Información Principal */}
        <div className="bg-white rounded-2xl shadow-xl p-6 mb-6">
          <div className="flex flex-col md:flex-row gap-6">
            {/* Foto de Perfil */}
            <div className="flex-shrink-0">
              <div className="relative">
                <img
                                        src={profile.photoURL || '/default-avatar.png'}
                  alt={`${profile.firstName} ${profile.lastName}`}
                  className="w-32 h-32 rounded-full object-cover border-4 border-white shadow-lg"
                />
                <div className="absolute -bottom-2 -right-2">
                  {getSectorIcon(profile.sector)}
                </div>
              </div>
              
              {/* QR Code */}
              <div className="mt-4 flex justify-center">
                <div className="bg-white p-2 rounded-lg shadow-md">
                  {profile.qrCodeUrl ? (
                    <img 
                      src={profile.qrCodeUrl} 
                      alt="QR Code" 
                      className="w-20 h-20"
                    />
                  ) : (
                    <QRCodeSVG
                      value={`https://brix-v2.web.app/worker/${workerId}`}
                      size={80}
                      level="M"
                    />
                  )}
                </div>
              </div>
            </div>

            {/* Información Básica */}
            <div className="flex-1">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h1 className="text-3xl font-bold text-gray-800 mb-2">
                    {profile.firstName} {profile.lastName}
                  </h1>
                  <div className="flex items-center gap-3 mb-3">
                    {getRoleBadge(profile.role)}
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      profile.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {t(`publicProfile.status.${profile.status}`)}
                    </span>
                  </div>
                </div>
                <div className="flex-shrink-0">
                  <img
                    src="https://firebasestorage.googleapis.com/v0/b/brix-v2.firebasestorage.app/o/Marca%2FRecurso%205.svg?alt=media&token=65d56a39-541f-4ec0-864f-fc2105cf9c54"
                    alt="Brix Logo"
                    className="h-8 w-auto"
                  />
                </div>
              </div>

              {/* Estadísticas */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className="text-center p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center justify-center gap-2 mb-1">
                    <FaTrophy className="text-yellow-500" />
                    <span className="text-lg font-bold text-gray-800">{profile.completedJobs || 0}</span>
                  </div>
                  <p className="text-sm text-gray-600">{t('publicProfile.completedJobs')}</p>
                </div>
                
                <div className="text-center p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center justify-center gap-2 mb-1">
                    <FaStar className="text-yellow-500" />
                    <span className="text-lg font-bold text-gray-800">{(profile.rating || 0).toFixed(1)}</span>
                  </div>
                  <p className="text-sm text-gray-600">{t('publicProfile.rating')} ({profile.reviews || 0} {t('publicProfile.reviews')})</p>
                </div>
                
                <div className="text-center p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center justify-center gap-2 mb-1">
                    <FaCalendarAlt className="text-blue-500" />
                    <span className="text-lg font-bold text-gray-800">{profile.experience?.years || 0}</span>
                  </div>
                  <p className="text-sm text-gray-600">{t('publicProfile.yearsExperience')}</p>
                </div>
              </div>

              {/* Información de Contacto */}
              <div className="space-y-2">
                <div className="flex items-center gap-3">
                  <FaMapMarkerAlt className="text-gray-400" />
                  <span className="text-gray-700">
                    {profile.address?.street} {profile.address?.number}, {profile.address?.apartment && `Apt ${profile.address.apartment}, `}{profile.address?.city}, {profile.address?.state} {profile.address?.zipCode}, {profile.address?.country}
                  </span>
                </div>
                
                <div className="flex items-center gap-3">
                  <FaEnvelope className="text-gray-400" />
                  <span className="text-gray-700">{profile.email}</span>
                </div>
                
                {profile.phone && (
                  <div className="flex items-center gap-3">
                    <FaPhone className="text-gray-400" />
                    <span className="text-gray-700">{profile.phone}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Descripción Personal */}
        {profile.personalDescription && (
          <div className="bg-white rounded-2xl shadow-xl p-6 mb-6">
            <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
              <FaUser />
              {t('profile.personalDescription')}
            </h3>
            
            <div>
              <p className="text-gray-700 leading-relaxed">
                {profile.personalDescription}
              </p>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Experiencia */}
          <div className="bg-white rounded-2xl shadow-xl p-6">
            <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
              <FaBriefcase />
              {t('publicProfile.experience')}
            </h3>
            
            <div className="space-y-3">
              <div>
                <p className="text-gray-700">{profile.experience?.description}</p>
              </div>
            </div>
          </div>

          {/* Certificaciones Aprobadas */}
          <div className="bg-white rounded-2xl shadow-xl p-6">
            <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
              <FaGraduationCap />
              {t('publicProfile.certifications')} ({approvedCertifications.length})
            </h3>
            
            <div className="space-y-3">
              {approvedCertifications.length === 0 ? (
                <p className="text-gray-500 text-center py-4">{t('publicProfile.noCertifications')}</p>
              ) : (
                approvedCertifications.map((cert) => (
                  <div key={cert.id} className="border border-gray-200 rounded-lg p-3">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h4 className="font-semibold text-gray-800 mb-1">{cert.name}</h4>
                        <p className="text-sm text-gray-600">{t('publicProfile.issuer')}: {cert.issuer}</p>
                        <p className="text-sm text-gray-600">{t('publicProfile.issueDate')}: {cert.date}</p>
                        {cert.expiryDate && (
                          <p className="text-sm text-gray-600">{t('publicProfile.expiryDate')}: {cert.expiryDate}</p>
                        )}
                      </div>
                      
                      <div className="flex items-center gap-1 text-green-600">
                        <FaCheck />
                        <span className="text-xs">{t('publicProfile.verified')}</span>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Identificación */}
        <div className="bg-white rounded-2xl shadow-xl p-6 mt-6">
          <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
            <FaIdCard />
            {t('profile.identification')}
          </h3>
          
          {profile.identification ? (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('profile.idType')}
                  </label>
                  <p className="text-gray-800">
                    {profile.identification.type === 'driver_license' ? t('profile.driverLicense') :
                     profile.identification.type === 'passport' ? t('profile.passport') :
                     profile.identification.type === 'national_id' ? t('profile.nationalId') :
                     t('profile.other')}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('profile.idNumber')}
                  </label>
                  <p className="text-gray-800">{profile.identification.number}</p>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-8">
              <FaIdCard className="text-gray-400 text-4xl mx-auto mb-2" />
              <p className="text-gray-600 font-medium">{t('profile.noIdentification')}</p>
            </div>
          )}
        </div>

        {/* Habilidades */}
        <div className="bg-white rounded-2xl shadow-xl p-6 mt-6">
          <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
            <FaTools />
            {t('publicProfile.skills')} ({profile.experience?.skills?.length || 0})
          </h3>

          {/* Lista de habilidades */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {!profile.experience?.skills || profile.experience.skills.length === 0 ? (
              <div className="col-span-2 text-center py-8">
                <FaTools className="text-gray-400 text-4xl mx-auto mb-2" />
                <p className="text-gray-600 font-medium">{t('publicProfile.noSkills')}</p>
              </div>
            ) : (
              profile.experience.skills.map((skill) => (
                <div key={skill.id} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-start justify-between mb-2">
                    <h4 className="font-semibold text-gray-800">
                      {i18n.language === 'es' ? skill.name_es : skill.name_en}
                    </h4>
                    <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      {getCategoryName(skill.categoryId)}
                    </span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default WorkerPublicProfile; 